import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import {
  getCompanyAgents,
  getCompanyBranches,
  getCompanyTeams,
  getTags,
} from './service';
import {
  AGENT,
  BRANCH,
  TEAM,
} from '@/components/FilterBar/constants/filtersConstants';

const SCOPE_TYPES = [AGENT, BRANCH, TEAM];

function rememberLeadScope(state) {
  state.leadScope = state.filters.selectedIds.filter((item) =>
    SCOPE_TYPES.includes(item.filter_type)
  );
}

export const FilterBarSlice = createSlice({
  name: 'FilterBar',
  initialState,
  reducers: {
    setSelectedIds: (state, action) => {
      const { idList, type } = action.payload;

      // Remove all existing items of the same type
      let filteredIds = state.filters.selectedIds.filter(
        (item) => item.filter_type !== type
      );

      // Agent, branch and team are mutually exclusive on the backend
      // (precedence agent_ids > branch_ids > team_ids, never combined) —
      // applying one clears the other two here.
      if (type === BRANCH) {
        filteredIds = filteredIds.filter(
          (item) => item.filter_type !== AGENT && item.filter_type !== TEAM
        );
      }

      if (type === AGENT) {
        filteredIds = filteredIds.filter(
          (item) => item.filter_type !== BRANCH && item.filter_type !== TEAM
        );
      }

      if (type === TEAM) {
        filteredIds = filteredIds.filter(
          (item) => item.filter_type !== AGENT && item.filter_type !== BRANCH
        );
      }

      // Add the new items of the specified type
      state.filters.selectedIds = [...filteredIds, ...idList];
      if (
        state.trackLeadScope &&
        !action.payload.ephemeral &&
        SCOPE_TYPES.includes(type)
      ) {
        rememberLeadScope(state);
      }
    },

    setTrackLeadScope: (state, action) => {
      state.trackLeadScope = action.payload;
    },

    setAgentsByBranch: (state, action) => {
      const branchesId = action.payload;
      if (!branchesId.length) {
        state.filters.selectAgents = state.filters.companyAgents;
        return;
      }
      const updatedAgents = state.filters.companyAgents.filter((agent) =>
        branchesId.includes(agent.branch.id)
      );
      state.filters.selectAgents = updatedAgents;
    },

    markFiltersBootstrapped: (state) => {
      state.filtersBootstrapped = true;
      if (!state.leadScope) rememberLeadScope(state);
    },

    restoreLeadScope: (state, action) => {
      const others = state.filters.selectedIds.filter(
        (item) => !SCOPE_TYPES.includes(item.filter_type)
      );
      state.filters.selectedIds = [...others, ...action.payload];
    },

    removeTag: (state, action) => {
      if (!action.payload) {
        state.filters.selectedIds = [];
        if (state.trackLeadScope) rememberLeadScope(state);
        return;
      }

      const { id, filter_type } = action.payload;
      state.filters.selectedIds = state.filters.selectedIds.filter(
        (item) => item.id !== id || item.filter_type !== filter_type
      );
      if (state.trackLeadScope && SCOPE_TYPES.includes(filter_type)) {
        rememberLeadScope(state);
      }
    },
  },

  extraReducers: (builder) => {
    // Get agents
    builder.addCase(getCompanyAgents.pending, (state) => {
      state.filters.loadingAgents = true;
      state.filters.errorAgents = null;
    });
    builder.addCase(getCompanyAgents.fulfilled, (state, action) => {
      const { users } = action.payload;
      state.filters.loadingAgents = false;
      state.filters.errorAgents = null;
      state.filters.selectAgents = users;
      state.filters.companyAgents = users;
    });
    builder.addCase(getCompanyAgents.rejected, (state) => {
      state.filters.loadingAgents = false;
      state.filters.errorAgents = true;
    });

    // Get branches
    builder.addCase(getCompanyBranches.pending, (state) => {
      state.filters.loadingBranches = true;
      state.filters.errorBranches = null;
    });
    builder.addCase(getCompanyBranches.fulfilled, (state, action) => {
      const { branches } = action.payload;
      state.filters.loadingBranches = false;
      state.filters.errorBranches = null;
      state.filters.selectBranches = branches;
    });
    builder.addCase(getCompanyBranches.rejected, (state) => {
      state.filters.loadingBranches = false;
      state.filters.errorBranches = true;
    });

    // Get teams
    builder.addCase(getCompanyTeams.pending, (state) => {
      state.filters.loadingTeams = true;
      state.filters.errorTeams = null;
    });
    builder.addCase(getCompanyTeams.fulfilled, (state, action) => {
      const { teams } = action.payload;
      state.filters.loadingTeams = false;
      state.filters.errorTeams = null;
      state.filters.selectTeams = teams;
    });
    builder.addCase(getCompanyTeams.rejected, (state) => {
      state.filters.loadingTeams = false;
      state.filters.errorTeams = true;
    });

    // Get tags
    builder.addCase(getTags.pending, (state) => {
      state.filters.loadingTags = true;
    });
    builder.addCase(getTags.fulfilled, (state, action) => {
      state.filters.loadingTags = false;
      state.filters.tags = action.payload;
    });
    builder.addCase(getTags.rejected, (state) => {
      state.filters.loadingTags = false;
    });
  },
});

export const {
  setSelectedIds,
  setAgentsByBranch,
  markFiltersBootstrapped,
  restoreLeadScope,
  setTrackLeadScope,
  removeTag,
} = FilterBarSlice.actions;

export default FilterBarSlice.reducer;
